import { createWidgetFromPredesigned } from './predesignedCardRegistry';
import { dataApi } from '../../config/axios';
// TemplateInfo interface (converted from TypeScript)
// export interface TemplateInfo {
//   id: string;
//   name: string;
//   description: string;
//   category?: string;
//   thumbnail?: string;
// }

/**
 * Load a template by ID from the templates folder or generate default templates
 */
export async function loadTemplate(templateId) {
    // Check if this is a default template that should be generated
    if (templateId === 'annual-report-2025') {
        return generateAnnualReportTemplate();
    }

    try {
        const url = new URL(window.location.href);
        const params = new URLSearchParams(url.search);

        const level = params.get('level') || 'school';
        const levelId = params.get('levelId') || '';
        const period = params.get('period') || 'weekly';
        const interval = params.get('interval') || '1';
        const term = params.get('term') || '1';
        const year = params.get('year') || new Date().getFullYear().toString();

        // Build a new URLSearchParams object
        const queryParams = new URLSearchParams({
            level,
            levelId,
            period,
            interval,
            term,
            year,
        });

        const fetchedTemplate = await dataApi.get(`api/dynamic-reports/report-template?${queryParams.toString()}`);

        // const response = await fetch(`/templates/${templateId}.json`);
        // if (!response.ok) {
        //     throw new Error(`Template not found: ${templateId}`);
        // }
        const template = fetchedTemplate?.data?.data?.template;

        // Validate template structure
        if (!template.id || !template.name || !template.layout || !template.widgets || !template.metadata) {
            throw new Error('Invalid template format: missing required fields');
        }

        return template;
    } catch (error) {
        console.error('Error loading template:', error);
        throw error;
    }
}

/**
 * Get list of available templates
 */
export async function getAvailableTemplates() {
    const defaultTemplates = getDefaultTemplates();
    const defaultTemplateIds = new Set(defaultTemplates.map((t) => t.id));

    try {
        const response = await fetch('/templates/index.json');
        if (!response.ok) {
            // If index doesn't exist, return default templates
            return defaultTemplates;
        }
        const fetchedTemplates = await response.json();

        // Merge fetched templates with defaults, avoiding duplicates
        // Default templates take precedence if there's a conflict
        const fetchedTemplateIds = new Set(fetchedTemplates.map((t) => t.id));
        const uniqueFetchedTemplates = fetchedTemplates.filter((t) => !defaultTemplateIds.has(t.id));

        return [...defaultTemplates, ...uniqueFetchedTemplates];
    } catch (error) {
        console.error('Error loading template index:', error);
        // Return default templates on error
        return defaultTemplates;
    }
}

/**
 * Get default template list (fallback)
 */
function getDefaultTemplates() {
    return [
        {
            id: 'annual-report-2025',
            name: 'Annual Report 2025',
            description: 'Annual report template with engagement metrics, emotional states, and class comparisons',
            category: 'Annual Reports',
        },
        {
            id: 'week-6-term-1-2025',
            name: 'Week 6 - Term 1, 2025',
            description: 'Standard weekly report template with key metrics, trends, and insights',
            category: 'Weekly Reports',
        },
    ];
}

/**
 * Generate the default annual report template
 */
export function generateAnnualReportTemplate() {
    const currentYear = new Date().getFullYear() - 1;
    const widgets = {};
    const layout = [];

    // Card 0: Report Summary (Text Widget)
    const reportSummaryWidget = createWidgetFromPredesigned('lsgo-report-summary');
    if (reportSummaryWidget) {
        reportSummaryWidget.id = 'widget-report-summary';
        widgets[reportSummaryWidget.id] = reportSummaryWidget;
        layout.push({
            i: reportSummaryWidget.id,
            x: 0,
            y: 0,
            w: 12,
            h: 5,
            minW: 6,
            minH: 4,
        });
    }

    // Card 0.5: Insights (Insights List Widget)
    const insightsWidget = createWidgetFromPredesigned('lsgo-insights-list');
    if (insightsWidget) {
        insightsWidget.id = 'widget-insights';
        const insightsConfig = insightsWidget.config;
        insightsConfig.dateRange = { period: 'year', year: currentYear };
        widgets[insightsWidget.id] = insightsWidget;
        layout.push({
            i: insightsWidget.id,
            x: 0,
            y: 5,
            w: 12,
            h: 12,
            minW: 6,
            minH: 4,
        });
    }

    // Card 1: Grade Engagement (Student Engagement Table - renamed)
    const gradeEngagementWidget = createWidgetFromPredesigned('lsgo-student-engagement-table');
    if (gradeEngagementWidget) {
        gradeEngagementWidget.id = 'widget-grade-engagement';
        gradeEngagementWidget.title = 'Grade Engagement';
        // Update engagement metric config for full year, term windows, weekly aggregation
        const tableConfig = gradeEngagementWidget.config;
        tableConfig.dateRange = { period: 'year', year: currentYear };
        tableConfig.engagementMetric = {
            entityType: 'student',
            period: {
                type: 'year',
                year: currentYear,
            },
            window: {
                unit: 'week', // Weekly windows
                size: 1,
                alignment: 'term', // Aligned to term dates
            },
            metric: {
                type: 'distinct_days',
                minEventsPerDay: 1,
                threshold: 3,
            },
            aggregation: {
                type: 'fraction_of_windows_meeting_threshold',
                includeEmptyWindows: false,
            },
        };
        tableConfig.engagementTable = {
            period: 'year',
            window: 'term',
            windowCount: 4,
        };
        widgets[gradeEngagementWidget.id] = gradeEngagementWidget;
        layout.push({
            i: gradeEngagementWidget.id,
            x: 0,
            y: 17, // Report summary (5) + Insights (12) = 17
            w: 12,
            h: 20, // Increased by 3 units for better visibility
            minW: 6,
            minH: 8,
        });
    }

    // Card 2: State of Emotion (Pie Chart - renamed)
    const stateOfEmotionWidget = createWidgetFromPredesigned('lsgo-state-of-emotion-comparison');
    if (stateOfEmotionWidget) {
        stateOfEmotionWidget.id = 'widget-state-of-emotion';
        stateOfEmotionWidget.title = 'State of Emotion';
        const chartConfig = stateOfEmotionWidget.config;
        chartConfig.dateRange = { period: 'year', year: currentYear };
        // Update data source filters to match year period
        if (stateOfEmotionWidget.dataSources) {
            stateOfEmotionWidget.dataSources.forEach((ds) => {
                if (ds.filters) {
                    ds.filters.period = 'year';
                }
            });
        }
        widgets[stateOfEmotionWidget.id] = stateOfEmotionWidget;
        layout.push({
            i: stateOfEmotionWidget.id,
            x: 0,
            y: 34, // Report summary (5) + Insights (12) + Grade Engagement (17) = 34
            w: 6,
            h: 14, // Updated height
            minW: 4,
            minH: 6,
        });
    }

    // Card 3: Student Check-in Context (Context Comparison Chart)
    const contextComparisonWidget = createWidgetFromPredesigned('lsgo-context-comparison-chart');
    if (contextComparisonWidget) {
        contextComparisonWidget.id = 'widget-context-comparison';
        contextComparisonWidget.title = 'Student Check-in Context';
        const chartConfig = contextComparisonWidget.config;
        chartConfig.dateRange = { period: 'year', year: currentYear };
        // Update data source filters to match year period
        if (contextComparisonWidget.dataSources) {
            contextComparisonWidget.dataSources.forEach((ds) => {
                if (ds.filters) {
                    ds.filters.period = 'year';
                }
            });
        }
        widgets[contextComparisonWidget.id] = contextComparisonWidget;
        layout.push({
            i: contextComparisonWidget.id,
            x: 6,
            y: 34, // Report summary (5) + Insights (12) + Grade Engagement (17) = 34
            w: 6,
            h: 14, // Updated height
            minW: 4,
            minH: 6,
        });
    }

    // Card 4: Check-in Emotions (new table widget)
    const checkInEmotionsWidget = createWidgetFromPredesigned('lsgo-check-in-emotions-table');
    if (checkInEmotionsWidget) {
        checkInEmotionsWidget.id = 'widget-check-in-emotions';
        checkInEmotionsWidget.title = 'Check-in Emotions';
        const tableConfig = checkInEmotionsWidget.config;
        tableConfig.dateRange = { period: 'year', year: currentYear };
        tableConfig.checkInEmotions = {
            period: 'year',
            aggregationWindow: 'term',
            aggregationType: 'count',
            topN: 10,
        };
        widgets[checkInEmotionsWidget.id] = checkInEmotionsWidget;
        layout.push({
            i: checkInEmotionsWidget.id,
            x: 0,
            y: 48, // Adjusted for widgets above (34 + 14 = 48)
            w: 12,
            h: 12, // Increased height for better visibility
            minW: 6,
            minH: 6,
        });
    }

    // Card 5: Check-in % displayed by class (Class Engagement - renamed)
    const classEngagementWidget = createWidgetFromPredesigned('lsgo-class-engagement-bar');
    if (classEngagementWidget) {
        classEngagementWidget.id = 'widget-class-engagement';
        classEngagementWidget.title = 'Check-in % displayed by class';
        const chartConfig = classEngagementWidget.config;
        chartConfig.dateRange = { period: 'year', year: currentYear };
        // Update engagement metric for weekly aggregation with 3+ days, 1+ checkin
        chartConfig.engagementMetric = {
            entityType: 'class',
            period: {
                type: 'year',
                year: currentYear,
            },
            window: {
                unit: 'week',
                size: 1,
                alignment: 'iso_week',
            },
            metric: {
                type: 'distinct_days',
                minEventsPerDay: 1,
                threshold: 3,
            },
            aggregation: {
                type: 'fraction_of_windows_meeting_threshold',
                includeEmptyWindows: false,
            },
        };
        widgets[classEngagementWidget.id] = classEngagementWidget;
        layout.push({
            i: classEngagementWidget.id,
            x: 0,
            y: 60, // Adjusted for widgets above (48 + 12 = 60)
            w: 12,
            h: 37, // Updated height
            minW: 6,
            minH: 6,
        });
    }

    return {
        id: `annual-report-${currentYear}`,
        name: 'Annual Report 2025',
        layout: {
            lg: layout,
        },
        widgets,
        metadata: {
            createdAt: new Date().toISOString(),
            updatedAt: new Date().toISOString(),
            version: '1.0.0',
        },
    };
}

/**
 * Auto-save template if the report was loaded from a template
 * This is a fire-and-forget operation that saves the template file when cache is modified
 * TODO: Implement actual template saving functionality
 */
export async function autoSaveTemplateIfNeeded(report) {
    // Only auto-save if the report was loaded from a template
    if (!report.loadedTemplateId) {
        return;
    }

    // TODO: Implement template saving when saveTemplateToFile is available
    // For now, this is a no-op to prevent errors
    return;
}
